import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatTabChangeEvent } from '@angular/material/tabs'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { ACCOUNT_ACTIVE, AccountObject, Carrier, CreditPool, SERVICE_PROVIDER, SegmentType } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { compact, difference, identity, isEmpty, sortBy, uniq } from 'lodash-es'
import { BehaviorSubject, combineLatest, merge, of } from 'rxjs'
import { distinctUntilChanged, filter, map, mapTo, startWith, switchMap, take, tap } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { CreditPoolApiService } from 'src/api/credit-pool'
import { ValidateApiService } from 'src/api/validate'
import { CreditPoolService } from 'src/services/data/credit-pool.service'
import { createAccount, createAccountFailure, createAccountSuccess, selectAllAccounts, updateAccount, updateAccountFailure, updateAccountSuccess, updateAccountsBulk, updateAccountsBulkFailure, updateAccountsBulkSuccess } from 'src/app/store/accounts'
import { selectAllBusinessTypes } from 'src/app/store/business-types'
import { selectAllCarriers } from 'src/app/store/carriers'
import { selectAllConsignees } from 'src/app/store/consignees/consignees.selectors'
import { selectAllCountries } from 'src/app/store/countries'
import { selectAllCurrencies, selectCurrencyEntities } from 'src/app/store/currencies'
import { selectAllItemTypes } from 'src/app/store/item-types'
import { selectAllLocations, selectLocationEntities } from 'src/app/store/locations'
import { selectAllMeasures } from 'src/app/store/measures'
import { selectAllPaymentMethods } from 'src/app/store/payment-methonds'
import { selectAllPaymentReferences } from 'src/app/store/payment-references'
import { selectAllPricingTerms } from 'src/app/store/pricing-terms'
import { selectAllProductCategories } from 'src/app/store/product-categories'
import { selectAllProductTypes } from 'src/app/store/product-types'
import { selectAllProducts } from 'src/app/store/products'
import { selectAllUsers } from 'src/app/store/users'
import { ConfirmModalService } from 'src/components/confirm/confirm-modal.service'
import { isAtLeastAdmin } from 'src/services/data/users.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { fieldsReport } from 'src/shared/utils/fields-report'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'
import { CarriersService } from '../../../tracking-providers/carriers.service'
import { applyBulkLocations, newCarrierForm, newCompanyBulkForm, newCompanyForm, newCreditForm, patchCreditForm, readCompanyForm, readCreditForm } from './company-form'
import { FormGroup } from '@angular/forms'
import { environment } from 'src/environments/environment'

const tabs = {
  DETAILS: 0,
  PROFILE: 1,
  ADDRESSES: 2,
  LOCATIONS: 3,
  TAX_PRICING: 4,
  CREDIT: 5,
  CONTACTS: 6,
  DISTRIBUTION: 7,
  BANKING: 8,
  NOTES: 9,
} as const

export interface CompanyFormOptions {
  company: DeepReadonly<AccountObject>,
  isBulkEdit?: boolean,
  selectedCompanies?: DeepReadonly<AccountObject[]>,
}

@Component({
  selector: 'tc-company-form',
  templateUrl: './company-form.component.html',
  styleUrl: './company-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CompanyFormComponent extends OnDestroyMixin implements OnInit {
  private readonly AuthApi = inject(AuthApiService)
  private readonly Carriers = inject(CarriersService)
  private readonly ConfirmModal = inject(ConfirmModalService)
  private readonly CreditPoolApi = inject(CreditPoolApiService)
  private readonly CreditPools = inject(CreditPoolService)
  private readonly toaster = inject(ToasterService)
  private readonly ValidateApi = inject(ValidateApiService)
  private readonly store = inject(Store)
  private readonly actions$ = inject(Actions)
  private readonly changeDetectorRef = inject(ChangeDetectorRef)
  private readonly dialogRef = inject<MatDialogRef<CompanyFormComponent, void>>(MatDialogRef)
  protected readonly dialogData = inject<CompanyFormOptions>(MAT_DIALOG_DATA)


  // input
  protected readonly isReadonly = false
  protected readonly isBulkEdit = this.dialogData.isBulkEdit
  protected readonly selectedCompanies$ = new BehaviorSubject(this.dialogData.selectedCompanies)
  protected readonly company$ = new BehaviorSubject(this.isBulkEdit ? this.dialogData.selectedCompanies[0] : this.dialogData.company)
  protected readonly companyAccount$ = this.company$.pipe(map(company => company.account))
  protected readonly creditPool$ = new BehaviorSubject({} as DeepReadonly<CreditPool>)

  // permissions
  protected readonly isAdminManagerSuperuser = isAtLeastAdmin(this.AuthApi.currentUser)
  protected readonly isJuniorAdmin = this.AuthApi.currentUser.role === 'junior-administrator'
  protected readonly isTrader = this.AuthApi.currentUser.role === 'trader'
  protected readonly tabs = tabs

  readonly displayedTabs = Object.values(tabs).filter(tab => ({
    // tabs access
    [tabs.DETAILS]: true,
    [tabs.PROFILE]: true,
    [tabs.ADDRESSES]: true,
    [tabs.LOCATIONS]: true,
    [tabs.TAX_PRICING]: this.isAdminManagerSuperuser || this.isJuniorAdmin,
    [tabs.CREDIT]: this.isAdminManagerSuperuser || this.dialogData.company?.type === SERVICE_PROVIDER,
    [tabs.CONTACTS]: true,
    [tabs.DISTRIBUTION]: true,
    [tabs.BANKING]: this.isAdminManagerSuperuser || this.isJuniorAdmin,
    [tabs.NOTES]: this.isAdminManagerSuperuser || this.isJuniorAdmin || this.isTrader,
  }[tab] && (!this.isBulkEdit || {
    // bulk edit access
    [tabs.DETAILS]: true,
    [tabs.PROFILE]: true,
    [tabs.ADDRESSES]: false,
    [tabs.LOCATIONS]: environment.WA_17729_ENABLE_BULK_LOCATIONS,
    [tabs.TAX_PRICING]: false,
    [tabs.CREDIT]: false,
    [tabs.CONTACTS]: false,
    [tabs.BANKING]: false,
    [tabs.NOTES]: false,
  }[tab])))
  protected selectedTab = this.displayedTabs[0]
  protected get selectedTabIndex() {
    return this.displayedTabs.indexOf(this.selectedTab)
  }

  private loadedTabs = new Set([this.displayedTabs[0]])

  protected isTabLoaded(tabValue: typeof this.displayedTabs[number]): boolean {
    return this.loadedTabs.has(tabValue)
  }

  // form
  protected readonly companyForm = newCompanyForm(this.ValidateApi, this.company$.value)
  protected readonly creditForm = newCreditForm({} as CreditPool)
  protected readonly bulkForm = newCompanyBulkForm()
  protected readonly carrierForm = newCarrierForm()

  // state
  protected readonly inProgress$ = new BehaviorSubject<'save'|'loading'|undefined>(undefined)
  private skippedLocations: { name: string, locationIds: string[] }[] = []
  protected readonly fieldsReport$ = merge(
    this.companyForm.statusChanges.pipe(startWith('PENDING')),
    this.creditForm.statusChanges.pipe(startWith('PENDING')),
    this.carrierForm.statusChanges.pipe(startWith('PENDING')),
  ).pipe(
    switchMap(() => {
      const forms: FormGroup[] = [this.companyForm, this.creditForm]
      const isServiceProvider = this.companyForm.controls.type.value === SERVICE_PROVIDER
      if (isServiceProvider) forms.push(this.carrierForm)
      return fieldsReport(...forms)
    }),
    tap(x => console.warn('fields report', x))
  )
  protected readonly isDirtyAndValid$ = combineLatest([
    this.companyForm.statusChanges.pipe(startWith('PENDING')),
    this.creditForm.statusChanges.pipe(startWith('PENDING')),
    this.carrierForm.statusChanges.pipe(startWith('PENDING')),
  ]).pipe(
    map(() => {
      const isServiceProvider = this.companyForm.controls.type.value === SERVICE_PROVIDER
      const carrierValid = !isServiceProvider || this.carrierForm.valid
      return (this.companyForm.valid && this.companyForm.dirty ||
              !this.companyForm.invalid && this.creditForm.valid && this.creditForm.dirty) &&
             carrierValid
    }),
    distinctUntilChanged())
  protected readonly isInvalid$ = combineLatest([
    this.companyForm.statusChanges.pipe(startWith('PENDING')),
    this.creditForm.statusChanges.pipe(startWith('PENDING')),
    this.carrierForm.statusChanges.pipe(startWith('PENDING')),
  ]).pipe(
    map(() => {
      const isServiceProvider = this.companyForm.controls.type.value === SERVICE_PROVIDER
      const carrierInvalid = isServiceProvider && this.carrierForm.invalid
      return this.companyForm.invalid || this.creditForm.invalid && this.creditForm.dirty || carrierInvalid
    }),
    distinctUntilChanged())


  ngOnInit() {
    merge(
      combineLatest([
        this.displayedTabs.includes(tabs.DETAILS)
          ? this.store.pipe(select(selectAllAccounts), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.DETAILS)
          ? this.store.pipe(select(selectAllBusinessTypes), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.DETAILS)
          ? this.store.pipe(select(selectAllCarriers), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.ADDRESSES)
          ? this.store.pipe(select(selectAllConsignees), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.BANKING) || this.displayedTabs.includes(tabs.LOCATIONS)
          ? this.store.pipe(select(selectAllCountries), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.CREDIT) && this.company$.value?.account || this.displayedTabs.includes(tabs.TAX_PRICING)
          ? this.store.pipe(select(selectAllCurrencies), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.DETAILS)
          ? this.store.pipe(select(selectAllItemTypes), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.LOCATIONS) || this.displayedTabs.includes(tabs.TAX_PRICING)
          ? this.store.pipe(select(selectAllLocations), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.TAX_PRICING)
          ? this.store.pipe(select(selectAllMeasures), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.CREDIT) && this.company$.value?.account
          ? this.store.pipe(select(selectAllPaymentMethods), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.CREDIT) && this.company$.value?.account
          ? this.store.pipe(select(selectAllPaymentReferences), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.TAX_PRICING)
          ? this.store.pipe(select(selectAllPricingTerms), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.PROFILE)
          ? this.store.pipe(select(selectAllProductCategories), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.PROFILE)
          ? this.store.pipe(select(selectAllProducts), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.PROFILE)
          ? this.store.pipe(select(selectAllProductTypes), waitNotEmpty()) : of(false),
        this.displayedTabs.includes(tabs.CONTACTS) || this.displayedTabs.includes(tabs.DETAILS) || this.displayedTabs.includes(tabs.CREDIT)
          ? this.store.pipe(select(selectAllUsers), waitNotEmpty()) : of(false),
      ]).pipe(take(1), mapTo(undefined)),
      this.actions$.pipe(ofType(createAccount, updateAccount, updateAccountsBulk), mapTo('save')),
      this.actions$.pipe(ofType(createAccountSuccess, createAccountFailure, updateAccountSuccess, updateAccountFailure, updateAccountsBulkSuccess, updateAccountsBulkFailure), mapTo(undefined)),
    ).pipe(distinctUntilChanged(), untilComponentDestroyed(this)).subscribe(status => {
      this.inProgress$.next(status)
    })

    // Load credit pool data in background (doesn't block dialog)
    this.loadCreditPool()

    this.actions$.pipe(ofType(createAccountSuccess, updateAccountSuccess), untilComponentDestroyed(this)).subscribe((action) => {
      this.company$.next(action.account)
      this.companyForm.markAsUntouched()
      this.companyForm.markAsPristine()
    })

    this.actions$.pipe(ofType(updateAccountsBulkSuccess), untilComponentDestroyed(this)).subscribe((action) => {
      const updatedCompanies = this.selectedCompanies$.value.map(c => {
        const updated = action.accounts.find(a => a.account === c.account)
        return updated || c
      })
      this.selectedCompanies$.next(updatedCompanies)
      this.bulkForm.markAsUntouched()
      this.bulkForm.markAsPristine()
      this.showSkippedLocations(action.accounts.length)
    })

    this.actions$.pipe(ofType(updateAccountsBulkFailure), untilComponentDestroyed(this)).subscribe(() => {
      this.skippedLocations = []
    })

    this.companyAccount$.pipe(distinctUntilChanged(), filter(identity), untilComponentDestroyed(this)).subscribe(async account => {
      const ctrl = this.companyForm.controls.carrier_id
      const _company = await this.Carriers.getCompanyCarrierId(account)
      const carrierId = ctrl.value || _company?.carrier_id
      if (!carrierId) return
      try {
        // Invalidate cache for carriers so that the latest created carrier is added to cache
        await this.Carriers.invalidateCache()
        const carrier = await this.Carriers.getCachedById(carrierId)
        this.carrierForm.setValue({
          carrier_id: carrier.carrier_id,
          type: carrier.type,
          operating_countries: carrier.operating_countries || [],
        })
      } catch (err) {
        if (err.status === 404) {
          ctrl.setValue('')
          ctrl.markAsTouched()
          ctrl.markAsDirty()
        }
        this.toaster.error('Unable to load carrier data')
      }
    })
  }

  private async loadCreditPool() {
    const company = this.company$.value
    if (!company?.account) {
      return of(undefined).toPromise()
    }

    try {
      let creditPool = await this.CreditPools.getFor(company.account) || {} as CreditPool
      creditPool.in_transit_max = creditPool.in_transit_max || 0
      creditPool.deal_creation_max = creditPool.deal_creation_max || 0
      creditPool.max = creditPool.max || 0
      creditPool.attributes = creditPool.attributes || {}

      this.creditPool$.next(creditPool)

      // Update the existing form with the loaded data
      const wasPristine = this.creditForm.pristine
      patchCreditForm(this.creditForm, creditPool)
      this.changeDetectorRef.detectChanges()
      if (wasPristine) {
        this.creditForm.markAsPristine()
        this.creditForm.updateValueAndValidity()
      }
    } catch (error) {
      console.error('Error loading credit pool:', error)
    }
  }

  protected nextStep() {
    const index = this.displayedTabs.indexOf(this.selectedTab)
    this.selectedTab = this.displayedTabs[index + 1]
  }

  protected prevStep() {
    const index = this.displayedTabs.indexOf(this.selectedTab)
    this.selectedTab = this.displayedTabs[index - 1]
  }

  protected setSelectedTab(e: MatTabChangeEvent) {
    this.selectedTab = this.displayedTabs[e.index]
    this.loadedTabs.add(this.selectedTab)
  }

  protected save() {
    if (this.inProgress$.value) return

    if (this.isBulkEdit) this.saveMultipleCompanies()
    else this.saveSingleCompany()
  }

  protected async close() {
    if (this.companyForm.dirty || this.creditForm.dirty) {
      await this.ConfirmModal.showUnsavedDataWarning()
    }
    this.dialogRef.close()
  }

  private saveMultipleCompanies() {
    const bulk = this.bulkForm.getRawValue()
    this.skippedLocations = []
    const patches: { account: number, patch: DeepReadonly<Partial<AccountObject>>}[] = compact(this.selectedCompanies$.value.map(company => {
      let patch: DeepReadonly<Partial<AccountObject>> = undefined
      if (bulk.canEditManagers) {
        if (bulk.accountManagersToAdd.length) patch = { ...patch, managers: uniq((company.managers || []).concat(bulk.accountManagersToAdd)) }
        if (bulk.accountManagersToDelete.length) patch = { ...patch, managers: difference(patch?.managers || company.managers || [], bulk.accountManagersToDelete) }
        if (bulk.accountManagersToReplace.length) patch = { ...patch, managers: bulk.accountManagersToReplace }
      }
      if (bulk.canEditCoordinators) {
        if (bulk.coordinatorsToAdd.length) patch = { ...patch, coordinators: uniq((company.coordinators || []).concat(bulk.coordinatorsToAdd)) }
        if (bulk.coordinatorsToDelete.length) patch = { ...patch, coordinators: difference(patch?.coordinators || company.coordinators || [], bulk.coordinatorsToDelete) }
        if (bulk.coordinatorsToReplace.length) patch = { ...patch, coordinators: bulk.coordinatorsToReplace }
      }
      if (bulk.canEditManager && bulk.managerInCommon) patch = { ...patch, manager: bulk.managerInCommon }
      if (bulk.canEditCoordinator && bulk.coordinatorInCommon) patch = { ...patch, coordinator: bulk.coordinatorInCommon }
      if (bulk.canEditLanguage && bulk.languageInCommon) patch = { ...patch, language: bulk.languageInCommon }
      if (bulk.canEditLocations && environment.WA_17729_ENABLE_BULK_LOCATIONS) {
        const { locations, skipped } = applyBulkLocations(company, bulk)
        if (skipped.length) this.skippedLocations.push({ name: company.name, locationIds: skipped })
        if (locations) patch = { ...patch, attributes: { locations } }
      }
      if (patch) return { account: company.account, patch: { ...patch, type: company.type } }
      return undefined
    }))
    if (!patches.length && this.skippedLocations.length) {
      this.showSkippedLocations(0)
      return
    }
    this.store.dispatch(updateAccountsBulk({ patches }))
  }

  private async showSkippedLocations(updatedCount: number) {
    const skipped = this.skippedLocations
    this.skippedLocations = []
    if (!skipped.length) return

    const locations = await this.store.pipe(select(selectLocationEntities), take(1)).toPromise()
    const lines = [
      updatedCount
        ? `${updatedCount} ${updatedCount === 1 ? 'company was' : 'companies were'} updated successfully.`
        : 'No companies were updated.',
      '',
      'These locations were kept because they are the company\'s Default or Tax/Pricing (Incoterm) Location:',
      '',
      ...sortBy(skipped, 'name').map(({ name, locationIds }) =>
        `${name}: ${locationIds.map(id => locations?.[id]?.name || id).join(', ')}`),
    ]
    await this.ConfirmModal.show({
      title: 'Bulk edit completed',
      titleIcon: 'fas fa-exclamation-triangle',
      description: lines.join('\n'),
      confirmButtonText: 'Close',
      cancelButtonText: '',
    }).catch(() => undefined)
  }

  private async saveSingleCompany() {
    const companyForm = this.companyForm.getRawValue()
    this.companyForm.markAllAsTouched()

    if (companyForm.type === SERVICE_PROVIDER) {
      this.carrierForm.markAllAsTouched()
      if (this.carrierForm.invalid) return
    }

    if (companyForm.status === ACCOUNT_ACTIVE && (isEmpty(companyForm.coordinator) || (isEmpty(companyForm.managers) && isEmpty(companyForm.manager)))) {
      this.toaster.warning('Account cannot be switched to Active status without have an account manager and logistics coordinator assigned.')
      return
    } else if (isEmpty(companyForm.products_spec)) {
      this.toaster.warning('Product is required, please add product in profile tab.')
      return
    } else if (!this.companyForm.valid) {
      return
    }

    if (this.creditForm.dirty) {
      this.creditForm.markAllAsTouched()
      if (!this.creditForm.valid) return
    }

    // Pre-flight carrier name validation — prevent partial save (WA-21500)
    if (companyForm.type === SERVICE_PROVIDER && (this.carrierForm.dirty || this.companyForm.controls.name.dirty)) {
      const existing = await this.Carriers.findCarriersByName(companyForm.name)
      const duplicate = existing.find(c => c.account !== this.company$.value.account?.toString())
      if (duplicate) {
        this.toaster.error(`There is already a carrier that has the name "${companyForm.name}", please use another company name`)
        return
      }
    }

    const patch = readCompanyForm(this.companyForm)
    if (this.company$.value.account) {
      this.store.dispatch(updateAccount({ account: this.company$.value, patch }))
    } else {
      this.store.dispatch(createAccount({ account: patch }))
    }

    // Re-acquire the account info (now with updates!)
    const account = await this.company$.pipe(filter(c => !!c.account), take(1)).toPromise()

    // If either the carrier specific form has changes _or_ the overall name of the carrier has changed
    if (this.carrierForm.dirty || this.companyForm.controls.name.dirty) {
      // To ensure the Save button keeps showing a spinner when carrier is being created
      this.inProgress$.next('save')
      await this.saveCarrierForCurrentCompany(account)
      this.carrierForm.markAsUntouched()
      this.carrierForm.markAsPristine()
    }

    if (this.creditForm.dirty) {
      const currencies = await this.store.pipe(select(selectCurrencyEntities), waitNotEmpty(), take(1)).toPromise()
      const patch = readCreditForm(this.creditForm, currencies)
      const {user_id} = this.AuthApi.currentUser

      let creditPool = this.creditPool$.value.pool_id
        ? await this.updateCreditPool(account.account, { ...patch, user_id } as any) // TODO: remove "any" after updating @tradecafe/types@0.1.158
        : await this.createCreditPool(account.account, { ...patch, user_id } as any) // TODO: remove "any" after updating @tradecafe/types@0.1.158
      this.creditPool$.next(creditPool)
      this.creditForm.markAsUntouched()
      this.creditForm.markAsPristine()
    }
  }

  private createCreditPool(account: number, payload: DeepReadonly<Partial<CreditPool>>) {
    const {user_id} = this.AuthApi.currentUser
    return this.CreditPoolApi.create(account, {
      ...payload,
      account,
      attributes: {
        ...payload.attributes,
        creator_id: user_id,
      },
    }).then(({ data }) => {
      this.toaster.success('Credit saved successfully.')
      return data
    }, (err) => {
      console.error('Unable to create credit.', err)
      this.toaster.error('Unable to create credit.', err)
      throw err
    })
  }

  private updateCreditPool(account: number, payload: DeepReadonly<Partial<CreditPool>>) {
    return this.CreditPoolApi.update(account, payload).then(({ data }) => {
      this.toaster.success('Credit saved successfully.')
      return data
    }, (err) => {
      console.error('Unable to update credit.', err)
      this.toaster.error('Unable to update credit.', err)
      throw err
    })
  }

  private async saveCarrierForCurrentCompany(account: DeepReadonly<AccountObject>) {
    const {type: companyType, name} = this.companyForm.getRawValue()
    // for now we don't allow buyers and suppliers to have carriers
    if (companyType !== SERVICE_PROVIDER) return

    // carrier is present. we need to populate company name and archive/unarchive
    // depending on selected carrier types
    const {type: carrierTypes = [], carrier_id, operating_countries} = this.carrierForm.getRawValue()

    // UI doesn't know anything about company carrier
    if (!carrierTypes?.length && !carrier_id && !operating_countries?.length) return

    const carrier: Partial<Carrier> = {
      carrier_id,
      account: account.account.toString(),
      name,
      type: carrierTypes as SegmentType[],
      operating_countries: operating_countries as string[],
    }

    if (carrierTypes.length || operating_countries?.length) {
      // at least one carrier type or operating country is selected
      carrier.archived = false // unarchive
    } else if (carrier_id) {
      // carrier is present in DB, but user selected no carrier types on UI
      console.warn(`archive carrier "${carrier.name}" (${carrier_id})`)
      carrier.archived = true // archive
    } else {
      // no carrier in DB, no carrier types selected, no operating countries - there is nothing to do
      return undefined
    }

    const stored = await this.saveCarrier(account, carrier)
    if (this.companyForm.controls.carrier_id.value !== stored.carrier_id) {
      this.carrierForm.controls.carrier_id.setValue(stored.carrier_id)
      this.companyForm.controls.carrier_id.setValue(stored.carrier_id)
      this.store.dispatch(updateAccount({ account, patch: { attributes: { carrier_id: stored.carrier_id }}}))
    }
  }

  /**
   * Create or Update carrier, display notifications
   *
   * @private
   * @param {any} carrier
   * @returns
   */
  private async saveCarrier(account: DeepReadonly<AccountObject>, carrier: DeepReadonly<Partial<Carrier>>) {
    try {
      const stored = await this.storeCarrier(account, carrier);

      Object.assign(carrier, stored)

      return stored
    } catch (err) {
      if (err?.status === 409) {
        console.error(`There is already a carrier that has the name "${carrier.name}", please use another company name`)
        this.toaster.error(`There is already a carrier that has the name "${carrier.name}", please use another company name`)
      } else {
        console.error('Unable to save carrier', err)
        this.toaster.error('Unable to save carrier', err)
      }
      throw err
    }
  }

  private async storeCarrier(account: DeepReadonly<AccountObject>, carrier: DeepReadonly<Partial<Carrier>>) {
    if (carrier.carrier_id) {
      return this.Carriers.update(carrier);
    }

    const orphanCarrier = await this.Carriers.findOrphanCarrier(account.account.toString());
    if (orphanCarrier) {
      const carrierUpdate = { ...orphanCarrier, ...carrier, carrier_id: orphanCarrier.carrier_id };
      console.warn(`WA-17284: Found orphan carrier "${orphanCarrier.name}" (${orphanCarrier.carrier_id}), updating with new carrier "${carrier.name}" (${carrier.carrier_id})`, { carrierUpdate });

      return this.Carriers.update(carrierUpdate);
    }

    return this.Carriers.create(carrier);
  }
}
